<template>
  <v-dialog v-model="innerDialog" persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-if="!readonly"
        v-bind="attrs"
        v-on="on"
        class="mx-2"
        color="primary"
      >
        「新規登録」はこのボタンを押してください
      </v-btn>
    </template>
    <v-form ref="form" v-model="valid">
      <v-card>
        <v-card-title>
          <span class="headlline">{{ dialogTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col :cols="readonlyInfoGuideType ? 3 : 8">
                <!-- 情報タイプ -->
                <v-text-field
                  v-if="readonlyInfoGuideType"
                  :value="infoGuideTypeName"
                  readonly
                  hide-details
                ></v-text-field>
                <v-radio-group
                  v-else
                  v-model="innerInfoGuideTypeCode"
                  row
                  @change="validateRelationAll()"
                >
                  <v-radio
                    v-for="type in infoGuideTypes"
                    :key="type.code"
                    :label="type.name"
                    :value="type.code"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-spacer></v-spacer>
            </v-row>
            <v-row>
              <v-col cols="3" v-if="showsMunicipalities">
                <!-- 市町村 -->
                <v-text-field
                  v-if="readonlyInfoGuideType"
                  :value="municipName"
                  label="市町村"
                  readonly
                  hide-details
                ></v-text-field>
                <v-select
                  v-else
                  v-model="innerMunicipCode"
                  :items="municipalities"
                  :placeholder="config.PLACEHOLDER_SELECT"
                  label="市町村"
                  item-text="municip_name"
                  item-value="municip_code"
                  outlined
                ></v-select>
              </v-col>
            </v-row>
            <p style="margin: 5px 0 15px 0" v-if="!readonly">
              日本語の情報登録は必須です。<br />
              <template v-if="isNew">
                リンクURLを登録する場合、「プレビュー」ボタンで内容を確認して登録してください。
              </template>
              <template v-else>
                リンクURLを登録、変更する場合、「プレビュー」ボタンで内容を確認して登録してください。
              </template>
            </p>
            <!-- 一覧表 -->
            <v-data-table
              :headers="tableHeaders"
              :items="items"
              locale="ja-jp"
              class="row-header-table"
              disable-sort
              hide-default-footer
            >
              <template v-slot:[`item.title`]="{ item }">
                <!-- タイトル -->
                <v-textarea
                  v-if="!(readonly && !item.title)"
                  :ref="`title_${item.locale}`"
                  :value="item.title"
                  @input="
                    item.title = $event.replace(/(\r\n|\n|\r)/gm, '');
                    validateRelation(item.locale);
                  "
                  class="mt-2"
                  :outlined="!readonly"
                  :readonly="readonly"
                  :maxlength="item.locale == 'en' ? 40 : 20"
                  :rules="rulesOf[item.locale]"
                  rows="2"
                  counter
                  dense
                  @keydown.enter.prevent
                ></v-textarea>
              </template>
              <template v-slot:[`item.note`]="{ item }">
                <!-- 説明文 -->
                <v-textarea
                  v-if="!(readonly && !(item.title || item.note || item.url))"
                  :ref="`note_${item.locale}`"
                  :value="item.note"
                  @input="
                    item.note = $event.replace(/(\r\n|\n|\r)/gm, '');
                    validateRelation(item.locale);
                  "
                  class="mt-2"
                  :outlined="!readonly"
                  :readonly="readonly"
                  :maxlength="item.locale === 'en' ? 80 : 40"
                  rows="3"
                  counter
                  dense
                  @keydown.enter.prevent
                ></v-textarea>
              </template>
              <template v-slot:[`item.url`]="{ item }">
                <!-- リンク先ＵＲＬ -->
                <v-textarea
                  v-if="!(readonly && !item.url)"
                  :ref="`url_${item.locale}`"
                  :value="item.url"
                  @input="
                    item.url = $event.replace(/(\r\n|\n|\r)/gm, '');
                    item.urlPreviewed = !item.url;
                    validateRelation(item.locale);
                  "
                  class="mt-2"
                  :outlined="!readonly"
                  :readonly="readonly"
                  :maxlength="200"
                  rows="3"
                  counter
                  dense
                  :rules="rulesOf[item.locale]"
                  @keydown.enter.prevent
                ></v-textarea>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <!-- 操作 -->
                <v-btn
                  v-if="!(readonly && !item.url)"
                  small
                  class="mx-2"
                  color="primary"
                  :href="item.url"
                  target="_blank"
                  :disabled="!item.url"
                  @click="item.urlPreviewed = true"
                >
                  {{
                    readonly || (!!item.url && item.urlPreviewed)
                      ? "プレビュー済"
                      : "プレビュー"
                  }}
                </v-btn>
              </template>
            </v-data-table>
          </v-container>
          <v-card-actions v-if="readonly">
            <v-row>
              <v-spacer></v-spacer>
              <v-btn class="mx-2" color="primary" @click="handleClose">
                戻る
              </v-btn>
            </v-row>
          </v-card-actions>
          <v-card-actions v-else>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn
                class="mx-2"
                color="primary"
                :loading="loading"
                :disabled="!(valid && allPreviewed)"
                @click="handleSave"
              >
                {{ buttonName }}
              </v-btn>
              <v-btn
                class="mx-2"
                color="primary"
                :disabled="loading"
                @click="handleClose"
              >
                キャンセル
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-form>
    <!-- ダイアログ -->
    <error-dialog ref="errorDialog" />
  </v-dialog>
</template>

<script>
import config from "@/js/const";
import ErrorDialog from "@/components/ErrorDialog";
import messageUtils from "@/mixins/message-utils";

/**
 * 一覧表のヘッダーを作成する。
 * @function
 * @param {boolean} showsNote 説明文を表示するかどうかを示す値
 * @returns {Object} ヘッダー
 */
const createTableHeaders = showsNote => [
  {
    text: "言語",
    value: "localeName",
    width: "7em",
    class: "text-pre"
  },
  { text: "タイトル", value: "title", width: "23%" },
  {
    text: "説明文",
    value: "note",
    width: "30%",
    align: showsNote ? "" : " d-none"
  },
  {
    text: "リンク先ＵＲＬ",
    value: "url",
    width: showsNote ? "51%" : "81%"
  },
  { text: "操作", value: "actions", width: "10em" }
];

export default {
  model: {
    prop: "dialog",
    event: "input"
  },

  mixins: [messageUtils],

  components: {
    ErrorDialog
  },

  props: {
    // このダイアログを表示するかどうかを示す値
    dialog: Boolean,
    // ローディング状態を示す値
    loading: Boolean,
    // 読み取り専用かどうかを示す値
    readonly: Boolean,
    // バリデーションルール
    validationRules: Object,
    // 言語リスト
    locales: Array,
    // 主言語（日本語）コード
    primaryLocaleCode: String,
    // 情報タイプリスト
    infoGuideTypes: Array,
    // 市町村が必要な情報タイプコード
    municipInfoGuideTypeCodes: Array,
    // 説明文が必要な情報タイプコード
    noteInfoGuideTypeCodes: Array,
    // 情報タイプコード
    infoGuideTypeCode: String,
    // 市町村リスト
    municipalities: Array,
    // 市町村コード
    municipCode: String,
    // 新規登録かどうかを示す値
    isNew: Boolean,
    // 表示する値。このダイアログで編集した内容が反映される。
    items: Array
  },

  data: () => ({
    config,
    // フォームの検証が成功しているかどうかを示す値
    valid: false
  }),

  watch: {
    dialog(value) {
      // ダイアログが表示時に初期化する。
      if (value) {
        this.initialize();
      }
    }
  },

  computed: {
    /**
     * 言語別の入力ルール
     */
    rulesOf() {
      const itemOf = Object.fromEntries(
        this.items.map(item => [item.locale, item])
      );

      return Object.fromEntries(
        this.locales.map(({ code }) => [
          code,
          code === this.primaryLocaleCode
            ? [this.validationRules.required()]
            : [
                v =>
                  (!itemOf[code].title &&
                    (!this.showsNote || !itemOf[code].note) &&
                    !itemOf[code].url) ||
                  this.validationRules.required()(v)
              ]
        ])
      );
    },

    /**
     * ダイアログタイトル
     */
    dialogTitle() {
      return `情報ガイドマスタの${
        this.readonly ? "詳細" : this.isNew ? "登録" : "編集"
      }`;
    },

    /**
     * ボタン名
     */
    buttonName() {
      return this.isNew ? "登録" : "更新";
    },

    /**
     * 一覧表のヘッダー
     */
    tableHeaders() {
      return createTableHeaders(this.showsNote);
    },

    /**
     * 情報タイプと市町村を読み取り専用にするかどうかを示す値
     */
    readonlyInfoGuideType() {
      return this.readonly || !this.isNew;
    },

    /**
     * 情報ガイドコードをキーに情報ガイドを保持するオブジェクト
     */
    infoGudeTypeFrom() {
      return Object.fromEntries(
        this.infoGuideTypes.map(type => [type.code, type])
      );
    },

    /**
     * 情報タイプ名
     */
    infoGuideTypeName() {
      return this.infoGudeTypeFrom[this.infoGuideTypeCode]?.name ?? "";
    },

    /**
     * 市町村コードをキーに市町村を保持するオブジェクト
     */
    municipFrom() {
      return Object.fromEntries(
        this.municipalities.map(municip => [municip.municip_code, municip])
      );
    },

    /**
     * 市町村名
     */
    municipName() {
      return this.municipFrom[this.municipCode]?.municip_name ?? "";
    },

    /**
     * 市町村を表示するかどうかを示す値
     */
    showsMunicipalities() {
      return this.municipInfoGuideTypeCodes.includes(this.infoGuideTypeCode);
    },

    /**
     * 説明文を表示するかどうかを示す値
     */
    showsNote() {
      return this.noteInfoGuideTypeCodes.includes(this.infoGuideTypeCode);
    },

    /**
     * 入力されたリンク先ＵＲＬが全てプレビュー済みかどうかを示す値
     */
    allPreviewed() {
      return this.items.every(item => !item.url || item.urlPreviewed);
    },

    /**
     * このダイアログを表示するかどうかを示す値
     */
    innerDialog: {
      get() {
        return this.dialog;
      },
      set(newValue) {
        this.$emit("input", newValue);
      }
    },

    /**
     * 情報タイプコード
     */
    innerInfoGuideTypeCode: {
      get() {
        return this.infoGuideTypeCode;
      },
      set(newValue) {
        this.$emit("update:infoGuideTypeCode", newValue);
      }
    },

    /**
     * 市町村コード
     */
    innerMunicipCode: {
      get() {
        return this.municipCode;
      },
      set(newValue) {
        this.$emit("update:municipCode", newValue);
      }
    }
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    initialize() {
      this.$nextTick(() => {
        // スクロールのリセット
        const elements = document.getElementsByClassName("v-dialog--active");
        if (elements && elements.length > 0) {
          elements[0].scrollTop = 0;
        }
        // バリデーションのリセット
        this.$refs.form.resetValidation();
      });
    },

    /**
     * 主言語以外の関連項目を検証する
     */
    validateRelationAll() {
      this.$nextTick(() => {
        this.locales
          .map(({ code }) => code)
          .forEach(code => {
            if (code !== this.primaryLocaleCode) {
              this.$refs[`title_${code}`].validate(true);
              this.$refs[`url_${code}`].validate(true);
            }
          });
      });
    },

    /**
     * 関連項目を検証する
     * @param {string} locale 言語コード
     */
    validateRelation(locale) {
      if (locale === this.primaryLocaleCode) {
        return;
      }

      this.$nextTick(() => {
        this.$refs[`title_${locale}`].validate(true);
        this.$refs[`url_${locale}`].validate(true);
      });
    },

    /**
     * 登録または更新ボタンのクリックイベントを処理する。
     */
    handleSave() {
      // 入力フォームのバリデーションチェック
      if (!this.$refs.form.validate()) {
        return;
      }

      this.$emit("save");
    },

    /**
     * キャンセルまたは戻るボタンのクリックイベントを処理する。
     */
    handleClose() {
      this.$emit("close");
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    async showErrorDialog(...messages) {
      await this.$refs.errorDialog.open(messages);
    }
  }
};
</script>

<style scoped>
.row-header-table >>> td:first-child {
  background-color: #e0e0e0;
  font-size: 0.75em;
  user-select: none;
}

.row-header-table >>> td:last-child {
  padding: 0;
}

.row-header-table >>> td:not(:first-child):not(:last-child) {
  vertical-align: top;
}
</style>
